import { useEffect, useState } from "react";
import { Sparkles, Loader2, CheckCircle2, XCircle } from "lucide-react";

/**
 * AI job progress indicator with elapsed timer + indeterminate bar.
 *
 * Props:
 *   status: 'queued' | 'processing' | 'completed' | 'failed' | null
 *   startedAt: Date | number — poll start time (used for elapsed counter)
 *   label:  optional headline (e.g. "Regenerating biography & summary")
 *   sub:    optional subtext
 *   expectedSeconds: default 45 — used to show phase hints
 *   compact: bool — smaller inline variant
 */
export default function AIProgressIndicator({
    status = "processing",
    startedAt,
    label = "AI content generation in progress",
    sub = "Grounded on your onboarding data — this typically takes 15–45 seconds.",
    expectedSeconds = 45,
    compact = false,
}) {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        if (status !== "queued" && status !== "processing") return;
        const id = setInterval(() => setNow(Date.now()), 250);
        return () => clearInterval(id);
    }, [status]);

    const elapsedMs = startedAt ? Math.max(0, now - Number(startedAt)) : 0;
    const secs = Math.floor(elapsedMs / 1000);
    // Estimate is capped at 95% so the bar never suggests instant completion.
    const pct = Math.min(95, Math.round((elapsedMs / (expectedSeconds * 1000)) * 100));

    const phase = (() => {
        if (status === "completed") return "Complete";
        if (status === "failed") return "Failed";
        if (secs < 3) return "Queued — waiting for worker";
        if (secs < 10) return "Reading your onboarding data";
        if (secs < 25) return "Drafting biography & summary";
        if (secs < 40) return "Refining SEO meta & headings";
        return "Finalising output — almost there";
    })();

    const done = status === "completed";
    const failed = status === "failed";
    const active = !done && !failed;

    const Icon = done ? CheckCircle2 : failed ? XCircle : Loader2;
    const barColor = done ? "bg-[#15803D]" : failed ? "bg-[#DC2626]" : "bg-gradient-to-r from-[#0F766E] via-[#2563EB] to-[#0F766E]";
    const borderColor = done ? "border-[#15803D]/30 bg-[#15803D]/5" : failed ? "border-[#DC2626]/30 bg-[#DC2626]/5" : "border-[#2563EB]/25 bg-[#2563EB]/5";
    const iconTint = done ? "text-[#15803D]" : failed ? "text-[#DC2626]" : "text-[#2563EB]";

    if (compact) {
        return (
            <div className={"inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-[12px] font-medium " + borderColor} data-testid="ai-progress-compact">
                <Icon size={13} className={`${iconTint} ${active ? "animate-spin" : ""}`} />
                <span>{phase}</span>
                {active && <span className="text-[#17212B]/50 font-mono">· {secs}s</span>}
            </div>
        );
    }

    return (
        <div className={"rounded-xl border p-4 " + borderColor} data-testid="ai-progress-indicator" role="status" aria-live="polite">
            <div className="flex items-start gap-3">
                <div className={"h-9 w-9 rounded-lg flex items-center justify-center shrink-0 " + (done ? "bg-[#15803D]/15" : failed ? "bg-[#DC2626]/15" : "bg-[#2563EB]/15")}>
                    <Sparkles size={16} className={iconTint} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-[14px] font-semibold text-[#0F172A]">{label}</div>
                        {active && (
                            <span className="inline-flex items-center gap-1 text-[11px] font-mono text-[#17212B]/60" data-testid="ai-progress-elapsed">
                                <Loader2 size={11} className="animate-spin text-[#2563EB]" />
                                {secs}s elapsed
                            </span>
                        )}
                    </div>
                    <div className="text-[12px] text-[#17212B]/70 mt-1 leading-relaxed">{sub}</div>
                </div>
            </div>
            <div className="mt-4">
                <div className="h-1.5 rounded-full bg-[#0F172A]/8 overflow-hidden">
                    <div
                        className={`h-full ${barColor} ${active ? "bg-[length:200%_100%] animate-[ai-progress-slide_2s_linear_infinite]" : ""} transition-all duration-500 ease-out`}
                        style={{ width: done ? "100%" : failed ? "100%" : `${Math.max(6, pct)}%` }}
                        data-testid="ai-progress-bar"
                    />
                </div>
                <div className="mt-2 flex items-center justify-between text-[11px] text-[#17212B]/55">
                    <span data-testid="ai-progress-phase">{phase}</span>
                    {active && <span>Est. ~{expectedSeconds}s total</span>}
                    {done && <span className="text-[#15803D] font-semibold">Ready to review</span>}
                    {failed && <span className="text-[#DC2626] font-semibold">See error message below</span>}
                </div>
            </div>
        </div>
    );
}
